/**
 * SentryProvider is a React context provider component responsible for integrating Sentry error tracking
 * with the application's user and appointment context. It listens for changes in the authenticated user data
 * and appointment details from the Redux store, and updates the Sentry context accordingly.
 *
 * - When the appointment details change, it sets or clears the appointment context in Sentry.
 * - When the authenticated user data changes, it sets or clears the user context in Sentry.
 *
 * This ensures that all Sentry error reports are enriched with the latest user and appointment information,
 * providing better traceability and debugging capabilities.
 *
 * @param children - The React child components that will be rendered within this provider.
 */
import React, { useEffect } from 'react';
import { useAppSelector } from '../hooks/customReduxHooks';
import sentryService from '../services/sentryService';
import { selectAppointmentDetails } from '../store/appointmentDetailsSlice';
import { selectAuthUserData } from '../store/auth';



const SentryProvider = ({ children } : { children: React.ReactNode }) => {
    const appointment = useAppSelector(selectAppointmentDetails);
    const authUserData = useAppSelector(selectAuthUserData);

    useEffect(() => {
        if (appointment) {
            sentryService.setAppointmentContext(appointment);
            sentryService.setTag('appointmentId', appointment.id.toString());
        } else {
            sentryService.setAppointmentContext({});
            sentryService.setTag('appointmentId', '');
        }
    }, [appointment]);

    useEffect(() => {
        if (authUserData) {
            sentryService.setUser({userId: authUserData.userId, email: authUserData.email});
        } else {
            sentryService.clearUser();
        }
    }, [authUserData]);


    return <>{children}</>;
};

export default SentryProvider;
